import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import InsightsFixedTip from './insightsFixedTip';
import GoalImage from '../../assets/images/goal-scored-1500x583.png';
import PoolImage from '../../assets/images/pool-location-1500x1744_new.jpeg';
import ReplaceStrings from '../../config/replaceStrings';
import Routes from '../../config/routes';
import { ZONE_IDS, ZONE_LABELS, ZoneId, zoneRectPercent } from '../../helpers/goalTargetFrame';
import {
    POOL_ZONE_IDS,
    POOL_ZONE_LABELS,
    PoolZoneId,
    poolZoneRectPercent,
} from '../../helpers/poolShotZones';
import { cellSavePctText, ZoneCounts } from './insightsGoalkeeperZones';
import {
    asOppositeGoalSide,
    asOppositePoolSide,
    formatShotResultCoverage,
    MapPoint,
    MapSide,
    pointsInGoalZone,
    pointsInPoolZone,
    tallyShotResults,
} from './insightsMapData';
import { cellShootPctText } from './insightsPlayerShotZones';

interface Props {
    side: MapSide;
    title: string;
    /** Pool plots where the shot was taken; goal plots where it was aimed. */
    surface: 'pool' | 'goal';
    emptyHint?: string;
    /** Draw zone grid (goal 3×3 or pool 6 attacking bins). */
    showZones?: boolean;
    /** Optional per-zone tallies shown inside grid cells. */
    zoneCounts?: ZoneCounts;
    /** When false, hide zone % / counts (grid borders stay). Default shown. */
    showZoneStats?: boolean;
    /** When false, hide colored shot dots (zone grid/% stay). Default shown. */
    showShotMarks?: boolean;
    /** Stretch photo across the panel (GK player maps). */
    fullBleed?: boolean;
    /**
     * Keeper: goals red, saves green.
     * Shooter: goals green, unsuccessful (saved/blocked/missed) red.
     * Default keeps match-report colours.
     */
    colorMode?: 'default' | 'keeper' | 'shooter';
    /**
     * Pool only: show the top half of the pool image (goal end).
     * Coord layer stays full-image height so Y 0–0.5 still aligns; y>0.5 clips off.
     */
    cropPoolTopHalf?: boolean;
}

const OUTCOME_LABELS: Record<string, string> = {
    goal: 'Goal',
    saved: 'Saved',
    blocked: 'Blocked',
    missed: 'Missed',
    exclusion: 'Exclusion',
    exclusionDrew: 'We drew (opp excluded)',
    exclusionGot: 'We got (we excluded)',
    penalty: 'Penalty',
};

const SHOOTER_OUTCOME_LABELS: Record<string, string> = {
    goal: 'Goal',
    saved: 'Miss',
    blocked: 'Miss',
    missed: 'Miss',
    exclusion: 'Exclusion',
};

const matchHref = (matchId: number): string =>
    Routes.ROUTE_INSIGHTS_MATCH.replace(ReplaceStrings.MATCH_ID, matchId.toString());

/** Read-only scatter over the same images the match report uses for logging. */
const InsightsShotMap: React.FC<Props> = ({
    side,
    title,
    surface,
    emptyHint,
    showZones,
    zoneCounts,
    showZoneStats = true,
    showShotMarks = true,
    fullBleed,
    colorMode = 'default',
    cropPoolTopHalf,
}) => {
    const [hoverKey, setHoverKey] = useState<string | null>(null);
    const surfaceRef = useRef<HTMLDivElement>(null);
    const [drill, setDrill] = useState<{
        zoneLabel: string;
        surface: 'pool' | 'goal';
        side: MapSide;
    } | null>(null);
    const tally = tallyShotResults(side, surface);
    const coverage = tally ? formatShotResultCoverage(tally) : undefined;
    const drawZones = !!showZones && side.points.length > 0;
    const zoneIds = surface === 'goal' ? ZONE_IDS : POOL_ZONE_IDS;
    const cropPool = surface === 'pool' && !!cropPoolTopHalf;
    const isShooter = colorMode === 'shooter';
    const isKeeper = colorMode === 'keeper';
    const outcomeLabels = isShooter ? SHOOTER_OUTCOME_LABELS : OUTCOME_LABELS;

    const colorClass =
        colorMode === 'keeper'
            ? 'isKeeperColors'
            : colorMode === 'shooter'
            ? 'isShooterColors'
            : '';

    const hover = hoverKey != null ? side.points.find((p) => p.key === hoverKey) : undefined;
    // Cropped pool plots in full-image Y; the visible surface is the top half.
    const hoverY = hover == null ? 0 : cropPool ? hover.y * 2 : hover.y;
    const hoverX = hover == null ? 0 : hover.x;

    useEffect(() => {
        if (!drill) return undefined;
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setDrill(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [drill]);

    const openZone = (zone: string): void => {
        if (surface === 'goal') {
            const inZone = pointsInGoalZone(side.points, zone as ZoneId);
            setDrill({
                zoneLabel: ZONE_LABELS[zone as ZoneId],
                surface: 'pool',
                side: asOppositePoolSide(inZone),
            });
            return;
        }
        const inZone = pointsInPoolZone(side.points, zone as PoolZoneId);
        setDrill({
            zoneLabel: POOL_ZONE_LABELS[zone as PoolZoneId],
            surface: 'goal',
            side: asOppositeGoalSide(inZone),
        });
    };

    const zoneOverlay = drawZones ? (
        <div className="insightsMapZoneGrid">
            {zoneIds.map((zone) => {
                const rect =
                    surface === 'goal'
                        ? zoneRectPercent(zone as ZoneId)
                        : poolZoneRectPercent(zone as PoolZoneId);
                const counts = zoneCounts?.[zone];
                const label = surface === 'pool' ? POOL_ZONE_LABELS[zone as PoolZoneId] : zone;
                const pct =
                    showZoneStats && counts && counts.shots > 0
                        ? isShooter
                            ? cellShootPctText(counts.goals, counts.shots)
                            : cellSavePctText(counts.saves, counts.goals)
                        : null;
                return (
                    <button
                        key={zone}
                        type="button"
                        className="insightsMapZoneCell isClickable"
                        style={{
                            left: `${rect.left}%`,
                            top: `${rect.top}%`,
                            width: `${rect.width}%`,
                            height: `${rect.height}%`,
                        }}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            openZone(zone);
                        }}
                    >
                        {pct ? <strong className="insightsMapZonePct">{pct}</strong> : null}
                        <em>{label}</em>
                        {showZoneStats && counts && counts.shots > 0 ? (
                            <small>
                                {isShooter
                                    ? `${counts.goals}G ${counts.saves}M`
                                    : `${counts.saves}Sv ${counts.goals}G`}
                            </small>
                        ) : null}
                    </button>
                );
            })}
        </div>
    ) : null;

    const renderDot = (point: MapPoint) => {
        const clickable = point.matchId != null;
        const className = [
            'insightsMapDot',
            `is-${point.outcome}`,
            hoverKey === point.key ? 'isActive' : '',
            clickable ? 'isClickable' : '',
            point.isPenalty ? 'isPenalty' : '',
        ]
            .filter(Boolean)
            .join(' ');
        const style: React.CSSProperties = {
            left: `${point.x * 100}%`,
            top: `${point.y * 100}%`,
        };
        const handlers = {
            onMouseEnter: () => setHoverKey(point.key),
            onMouseLeave: () => setHoverKey(null),
            onFocus: () => setHoverKey(point.key),
            onBlur: () => setHoverKey(null),
        };

        if (clickable) {
            return (
                <Link
                    key={point.key}
                    to={matchHref(point.matchId as number)}
                    className={className}
                    style={style}
                    title={point.label}
                    aria-label={point.label}
                    {...handlers}
                />
            );
        }

        return (
            <span
                key={point.key}
                className={className}
                style={style}
                title={point.label}
                {...handlers}
            />
        );
    };

    const dots = showShotMarks ? side.points.map(renderDot) : null;

    const miniDotClass = ['insightsMapDot', hover ? `is-${hover.outcome}` : '', colorClass]
        .filter(Boolean)
        .join(' ');

    const oppositeMini =
        hover == null ? null : surface === 'pool' && hover.goalX != null && hover.goalY != null ? (
            <div className={`insightsMapDotTipMini isGoal ${colorClass}`.trim()}>
                <img src={GoalImage} alt="" />
                <span
                    className={miniDotClass}
                    style={{ left: `${hover.goalX * 100}%`, top: `${hover.goalY * 100}%` }}
                />
            </div>
        ) : surface === 'goal' && hover.poolX != null && hover.poolY != null ? (
            <div className={`insightsMapDotTipMini isPool ${colorClass}`.trim()}>
                <div className="insightsMapCropFrame">
                    <img src={PoolImage} alt="" />
                    <span
                        className={miniDotClass}
                        style={{ left: `${hover.poolX * 100}%`, top: `${hover.poolY * 100}%` }}
                    />
                </div>
            </div>
        ) : null;

    const tooltip =
        hover != null ? (
            <InsightsFixedTip
                x={hoverX}
                y={hoverY}
                container={surfaceRef.current}
                className="insightsMapDotTip"
            >
                {hover.matchLine ? <b>{hover.matchLine}</b> : null}
                {hover.whenLine ? <i>{hover.whenLine}</i> : null}
                {hover.playerLine ? <span>{hover.playerLine}</span> : null}
                {hover.typeLine ? <span>{hover.typeLine}</span> : null}
                {hover.techniqueLine ? <span>{hover.techniqueLine}</span> : null}
                {hover.foulLine ? <span>{hover.foulLine}</span> : null}
                {hover.fakeLine ? <span>{hover.fakeLine}</span> : null}
                {hover.assistLine ? <span>{hover.assistLine}</span> : null}
                {hover.resultLine ? <span>{hover.resultLine}</span> : null}
                {hover.scoreAtLine ? <span>Score {hover.scoreAtLine}</span> : null}
                {hover.scoreLine ? <span>Final {hover.scoreLine}</span> : null}
                {oppositeMini}
                {hover.matchId != null ? <em>Click the dot to open the match</em> : null}
                {!hover.matchLine && !hover.whenLine ? <span>{hover.label}</span> : null}
            </InsightsFixedTip>
        ) : null;

    return (
        <div className={`insightsMap${fullBleed ? ' isFullBleed' : ''}`}>
            <div className="insightsMapHead">
                <span className="insightsMapTitle">{title}</span>
                {coverage ? <span className="insightsMapCoverage">{coverage}</span> : null}
            </div>

            {side.points.length === 0 ? (
                <div className="insightsEmpty">
                    {emptyHint ||
                        'No locations recorded. Tap the pool while logging shots to build this map.'}
                </div>
            ) : (
                <div
                    ref={surfaceRef}
                    className={[
                        surface === 'pool'
                            ? 'insightsMapSurface isPool'
                            : 'insightsMapSurface isGoal',
                        fullBleed ? 'isFullBleed' : '',
                        colorClass,
                        cropPool ? 'isCropTopHalf' : '',
                        hover != null ? 'isHovering' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                >
                    <div className="insightsMapClip">
                        {cropPool ? (
                            <div className="insightsMapCropFrame">
                                <img src={PoolImage} alt="Pool" />
                                {zoneOverlay}
                                {dots}
                            </div>
                        ) : (
                            <>
                                <img
                                    src={surface === 'pool' ? PoolImage : GoalImage}
                                    alt={surface === 'pool' ? 'Pool' : 'Goal'}
                                />
                                {zoneOverlay}
                                {dots}
                            </>
                        )}
                    </div>
                    {tooltip}
                </div>
            )}

            <div className="insightsMapLegend">
                {Object.keys(outcomeLabels)
                    .filter((outcome: string) =>
                        side.points.some((point: MapPoint) => point.outcome === outcome)
                    )
                    .filter((outcome: string, index: number, keys: string[]) => {
                        // Shooter mode collapses saved/blocked/missed → one "Miss" chip.
                        const label = outcomeLabels[outcome];
                        return keys.findIndex((k) => outcomeLabels[k] === label) === index;
                    })
                    .map((outcome: string) => (
                        <span key={outcomeLabels[outcome]}>
                            <i
                                className={`insightsMapDot is-${outcome}${
                                    isKeeper || isShooter ? ` ${colorClass}` : ''
                                }`}
                            />
                            {outcomeLabels[outcome]}
                        </span>
                    ))}
                {side.points.some((p) => p.isPenalty) ? (
                    <span>
                        <i className="insightsMapDot is-exclusionDrew isPenalty" />
                        Penalty (ring)
                    </span>
                ) : null}
            </div>

            {drill ? (
                <div
                    className="insightsMapDrill"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${drill.zoneLabel} locations`}
                    onClick={() => setDrill(null)}
                >
                    <div
                        className="insightsMapDrillCard"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="insightsMapDrillHead">
                            <h3>
                                {drill.surface === 'pool' ? 'Pool location' : 'Goalmouth'}
                                {' · '}
                                {drill.zoneLabel}
                            </h3>
                            <button
                                type="button"
                                className="insightsMapDrillClose"
                                aria-label="Close"
                                onClick={() => setDrill(null)}
                            >
                                ×
                            </button>
                        </div>
                        <InsightsShotMap
                            side={drill.side}
                            title={drill.surface === 'pool' ? 'Shot locations' : 'Goal targets'}
                            surface={drill.surface}
                            colorMode={colorMode}
                            cropPoolTopHalf={drill.surface === 'pool'}
                            fullBleed
                            showZones={false}
                            emptyHint="No opposite locations recorded for this zone."
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default InsightsShotMap;
